/**
 * "Lotes eliminados (auditoría)" (v2.2 findings.md #205).
 *
 * Current reports leave deleted lots out (findings #206) so their totals
 * match the screens. An audit needs to see them on purpose: one row per lot
 * deleted (or deleted and restored since) with when, by whom, and how many
 * of each kind of record it holds. Records are matched to a deleted lot with
 * the same rule Base de datos uses (`buildDeletedLotResolver`, findings
 * #200/#207); a restored lot is active again, so its records match by id.
 *
 * Pure and side-effect free.
 */
import { buildDeletedLotResolver, type DeletedLotRef } from './deletedLots'

export type DeletedLotAuditEntry = DeletedLotRef & {
  farmId: string
  createdAt?: string
  deletedByActorId?: string
  restoredAt?: string
  restoredByActorId?: string
}

type LotRecord = {
  lotId?: unknown
  lot?: unknown
  unmappedLot?: unknown
  productionUnitId?: unknown
  recordedAt?: unknown
  date?: unknown
}

export type DeletedLotAuditCounts = {
  countRecords: number
  mortalityRecords: number
  deaths: number
  weightSamples: number
  feedRecords: number
  feedKg: number
  harvestRecords: number
  harvestKg: number
  waterRecords: number
  firstRecordAt: string | null
  lastRecordAt: string | null
}

const parseNumber = (raw: unknown): number => {
  const num = typeof raw === 'number' ? raw : Number(String(raw ?? '').replaceAll(',', '.').trim())
  return Number.isFinite(num) ? num : 0
}

const emptyCounts = (): DeletedLotAuditCounts => ({
  countRecords: 0,
  mortalityRecords: 0,
  deaths: 0,
  weightSamples: 0,
  feedRecords: 0,
  feedKg: 0,
  harvestRecords: 0,
  harvestKg: 0,
  waterRecords: 0,
  firstRecordAt: null,
  lastRecordAt: null
})

export const buildDeletedLotAuditCounts = (args: {
  entries: readonly DeletedLotAuditEntry[]
  /** Lots of the farm as the device knows them now (active, incl. restored ones). */
  farmLots: ReadonlyArray<{ id: string; lotKey?: string; productionUnitId?: string; createdAt?: string }>
  fishStockLogs: readonly (LotRecord & { recordedAt: string })[]
  mortalityLogs: readonly (LotRecord & { recordedAt: string; mortalityCount?: unknown })[]
  weightSampleLogs: readonly (LotRecord & { recordedAt: string })[]
  feedLogs: readonly (LotRecord & { recordedAt: string; feedKg?: unknown })[]
  harvestLogs: readonly (LotRecord & { recordedAt: string; harvestKg?: unknown })[]
  waterRecords: readonly LotRecord[]
}): Map<string, DeletedLotAuditCounts> => {
  const result = new Map<string, DeletedLotAuditCounts>()
  args.entries.forEach((entry) => result.set(entry.id, emptyCounts()))

  const stillDeleted = args.entries.filter((entry) => !entry.restoredAt)
  const restoredIds = new Set(args.entries.filter((entry) => entry.restoredAt).map((entry) => entry.id))
  const resolveDeleted = buildDeletedLotResolver(stillDeleted, args.farmLots)
  const lotOf = (record: LotRecord): string | null => {
    const lotId = String(record.lotId ?? '').trim()
    if (lotId && restoredIds.has(lotId)) return lotId
    return resolveDeleted(record)
  }

  const visit = <T extends LotRecord>(records: readonly T[], apply: (counts: DeletedLotAuditCounts, record: T) => void) => {
    for (const record of records) {
      const lotId = lotOf(record)
      const counts = lotId ? result.get(lotId) : undefined
      if (!counts) continue
      apply(counts, record)
      const when = String(record.recordedAt ?? record.date ?? '').trim()
      if (when) {
        if (!counts.firstRecordAt || when < counts.firstRecordAt) counts.firstRecordAt = when
        if (!counts.lastRecordAt || when > counts.lastRecordAt) counts.lastRecordAt = when
      }
    }
  }

  visit(args.fishStockLogs, (counts) => { counts.countRecords += 1 })
  visit(args.mortalityLogs, (counts, log) => {
    counts.mortalityRecords += 1
    counts.deaths += parseNumber(log.mortalityCount)
  })
  visit(args.weightSampleLogs, (counts) => { counts.weightSamples += 1 })
  visit(args.feedLogs, (counts, log) => {
    counts.feedRecords += 1
    counts.feedKg += parseNumber(log.feedKg)
  })
  visit(args.harvestLogs, (counts, log) => {
    counts.harvestRecords += 1
    counts.harvestKg += parseNumber(log.harvestKg)
  })
  visit(args.waterRecords, (counts) => { counts.waterRecords += 1 })

  return result
}
